import * as React from 'react';
import { Formik, Form } from 'formik';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import * as Yup from 'yup';

import { TextInput } from 'components/Input';
import { Spacing, Page } from 'components/Layout';
import LrButton from 'components/LrButton';
import LrDialog from 'components/LrDialog';
import { currencyToNumber } from 'helpers/formatters';
import { currencyMask } from 'helpers/masks';
import { submitQuoteTaxes } from 'services/quotes';
import { Taxes, UnderwriterReview } from 'types/underwriting.type';

import SlaFees from './SlaFees';
import { TaxAndFeesInfo } from '../../../TaxAndFees';
import { CircularLoader } from '../../../Loader';
import { SubmissionDetails } from 'types/submission.type';
import { useSelector } from 'react-redux';
import { getProductLineTypesByCode } from 'application/redux/selectors';
import { calculateAdditionalBrokerPremium } from 'helpers/calculations';
import { getProductLineDisplayValue } from '../../../../helpers/submissions';

interface Props {
    isDialog?: boolean;
    isOpen?: boolean;
    uwFees: Record<string, UnderwriterReview>;
    submission: SubmissionDetails;
    onSuccess?: (taxes: Taxes[]) => void;
    onCancel?: () => void;
}

const TaxesDialog: React.FunctionComponent<Props> = (props) => {
    const { isDialog, isOpen, onCancel, onSuccess, uwFees, submission } = props;
    const productLineTypes = useSelector(getProductLineTypesByCode);

    // ValidationSchema
    const taxSchemaPart = Yup.object().shape({
        slaTax: Yup.string().required('Required').typeError('Required'),
    });
    const taxSchemaDef = {};
    Object.keys(uwFees).forEach((productCode) => (taxSchemaDef[productCode] = taxSchemaPart));
    const taxSchema = Yup.object().shape(taxSchemaDef);

    const getInitialValues = () => {
        const ret: Record<string, { slaFee?: string; slaTax?: string }> = {};
        Object.keys(uwFees).forEach(
            (productCode) =>
                (ret[productCode] = {
                    slaFee: undefined,
                    slaTax: undefined,
                })
        );
        return ret;
    };

    // Local State
    const [initialValues, setInitialValues] = React.useState<Record<string, { slaFee?: string; slaTax?: string }>>(
        getInitialValues()
    );

    const [action, setAction] = React.useState({ inProgress: false, error: false, success: false });
    const [taxesLoading, setTaxesLoading] = React.useState(
        Object.keys(uwFees).reduce((result, key) => {
            result[key] = !submission?.producer?.isFileTaxes;
            return result;
        }, {})
    ); // if the producer files their own taxes then we don't load in data
    const [isAutoCalc, setIsAutoCalc] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [submittedValues, setSubmittedValues] = React.useState<{ slaFee?: number; slaTax?: number } | undefined>();

    const doSubmit = async (values: any) => {
        try {
            const quoteTaxPromises: Promise<any>[] = [];
            const payloads: Taxes[] = [];
            Object.keys(uwFees).forEach((productCode) => {
                const payload = {
                    slaFee: values[productCode].slaFee ? currencyToNumber(values[productCode].slaFee) : 0,
                    slaTax: currencyToNumber(values[productCode].slaTax),
                };
                payloads.push(payload);
                quoteTaxPromises.push(submitQuoteTaxes(uwFees[productCode].id, payload));
            });
            await Promise.all(quoteTaxPromises);
            onSuccess && onSuccess(payloads);
        } catch (e) {
            setAction({ inProgress: false, error: true, success: false });
        }
    };

    const onSubmit = (values: Record<string, { slaFee?: string; slaTax?: string }>) => {
        setSubmittedValues(values);
        if (
            Object.values(values).every(
                (value) => !Number(value.slaTax?.replace('$', '')) && !Number(value.slaFee?.replace('$', ''))
            )
        ) {
            setConfirmOpen(true);
        } else {
            doSubmit(values);
        }
    };

    const confirmSubmit = () => {
        setConfirmOpen(false);
        doSubmit(submittedValues);
    };

    const checkValid = (valueProps: any) => taxSchema.isValidSync(valueProps.initialValues);
    const calculateTotalFees = (uwReview: UnderwriterReview) =>
        uwReview.inspectionFee + uwReview.policyFee + uwReview.professionalFee;

    const renderFormActions = (isValid: boolean, isSubmitting: boolean) => (
        <Grid container spacing={2} alignItems="center">
            {onCancel && (
                <Grid>
                    <LrButton lrType="text" onClick={onCancel}>
                        Cancel
                    </LrButton>
                </Grid>
            )}
            <Grid>
                <LrButton
                    lrType="primary"
                    type="submit"
                    disabled={!isValid}
                    isLoading={action.inProgress || isSubmitting}
                >
                    Next
                </LrButton>
            </Grid>
        </Grid>
    );

    const renderFormBody = (productCode: string, { values, handleChange, handleBlur, touched, errors }: any) => (
        <Grid container spacing={2} direction="column" style={{ margin: 0, width: '100%' }}>
            {/*<Grid md={isDialog ? 12 : 6} xs={12}>*/}
            <Grid xs={12}>
                <TextInput
                    title={`State Tax${isAutoCalc ? '(auto)' : ''}`}
                    name={`${productCode}.slaTax`}
                    placeholder="0.00"
                    value={values[productCode].slaTax}
                    onChange={(event) => {
                        setIsAutoCalc(false);
                        handleChange(event);
                    }}
                    onBlur={handleBlur}
                    mask={currencyMask}
                    errorMessage={touched[productCode]?.slaTax && errors[productCode]?.slaTax}
                />
            </Grid>
            {/*<Grid md={isDialog ? 12 : 6} xs={12}>*/}
            <Grid xs={12}>
                <TextInput
                    title={`Stamping Fee${isAutoCalc ? '(auto)' : ''}`}
                    name={`${productCode}.slaFee`}
                    placeholder="0.00"
                    value={values[productCode].slaFee}
                    onChange={(event) => {
                        setIsAutoCalc(false);
                        handleChange(event);
                    }}
                    onBlur={handleBlur}
                    mask={currencyMask}
                    errorMessage={touched[productCode]?.slaFee && errors[productCode]?.slaFee}
                />
            </Grid>
            <Spacing />
        </Grid>
    );

    // const renderDialogForm = () => (
    //     <Formik
    //         enableReinitialize
    //         initialValues={initialValues}
    //         isInitialValid={checkValid}
    //         onSubmit={onSubmit}
    //         validationSchema={taxSchema}
    //     >
    //         {(formProps) => (
    //             <Form>
    //                 {renderFormBody(formProps)}
    //                 {/*{renderFormActions(formProps.isValid, formProps.isSubmitting)}*/}
    //             </Form>
    //         )}
    //     </Formik>
    // );

    // const renderForm = () => (
    //     <Formik
    //         enableReinitialize
    //         initialValues={initialValues}
    //         isInitialValid={checkValid}
    //         onSubmit={onSubmit}
    //         validationSchema={taxSchema}
    //     >
    //         {(formProps) => (
    //             <Form>
    //                 <Page title="Add Taxes">
    //                     <Grid container spacing={1} direction="column">
    //                         <Grid>
    //                             <SlaFees
    //                                 homeState={uwFees.homeState}
    //                                 premium={uwFees.premium}
    //                                 totalFees={calculateTotalFees()}
    //                             />
    //                         </Grid>
    //                         <Grid container>{renderFormBody(formProps)}</Grid>
    //                     </Grid>
    //                 </Page>
    //                 <Spacing height="32px" />
    //                 {renderFormActions(formProps.isValid, formProps.isSubmitting)}
    //             </Form>
    //         )}
    //     </Formik>
    // );

    const renderSlaGrid = () => (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            isInitialValid={checkValid}
            onSubmit={onSubmit}
            validationSchema={taxSchema}
        >
            {(formProps) => (
                <Form>
                    <Grid container>
                        {Object.keys(formProps.values).map((productCode) => (
                            <Grid container spacing={1} direction="column">
                                <Grid>
                                    <Typography variant={'h4'}>
                                        {getProductLineDisplayValue(productLineTypes, productCode)}
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <SlaFees
                                        homeState={uwFees[productCode].homeState}
                                        premium={calculateAdditionalBrokerPremium(
                                            uwFees[productCode].isBrokerCommissionRated,
                                            uwFees[productCode].premium,
                                            uwFees[productCode].brokerCommissionPercentage
                                        )}
                                        totalFees={calculateTotalFees(uwFees[productCode])}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <TaxAndFeesInfo
                                        stateCode={uwFees[productCode].homeState}
                                        onTaxesAndFeesUpdate={(taxData) => {
                                            if (taxesLoading[productCode]) {
                                                setTaxesLoading({ ...taxesLoading, [productCode]: false });
                                                if (taxData.totalTaxRate) {
                                                    setIsAutoCalc(true);
                                                    const totalTaxable =
                                                        calculateAdditionalBrokerPremium(
                                                            uwFees[productCode].isBrokerCommissionRated,
                                                            uwFees[productCode].premium,
                                                            uwFees[productCode].brokerCommissionPercentage
                                                        ) +
                                                        (taxData.feesTaxed
                                                            ? calculateTotalFees(uwFees[productCode])
                                                            : 0);
                                                    const totalFeesRate = taxData?.totalFeesRate || 0;
                                                    const totalTaxRate = taxData?.totalTaxRate || 0;

                                                    const factor = Math.pow(10, taxData.roundingPrecisionDigits || 2);
                                                    const slaFee =
                                                        totalFeesRate >= 1
                                                            ? totalFeesRate
                                                            : Math.ceil(totalFeesRate * totalTaxable * factor) / factor;
                                                    const slaTax =
                                                        Math.ceil(totalTaxRate * totalTaxable * factor) / factor;
                                                    setInitialValues({
                                                        ...initialValues,
                                                        [productCode]: {
                                                            slaFee: String(slaFee),
                                                            slaTax: String(slaTax),
                                                        },
                                                    });
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid>
                                    {Object.values(taxesLoading).some((v) => v) ? (
                                        <CircularLoader fillHeight={'25vh'} />
                                    ) : (
                                        renderFormBody(productCode, formProps)
                                    )}
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                    {renderFormActions(formProps.isValid, formProps.isSubmitting)}
                </Form>
            )}
        </Formik>
    );

    if (!uwFees) return <></>;
    return (
        <>
            {isDialog && (
                <LrDialog
                    width={`${400 * Object.keys(uwFees).length}px`}
                    isOpen={isOpen}
                    title="Add Taxes"
                    onCancel={onCancel}
                >
                    {renderSlaGrid()}
                </LrDialog>
            )}
            {/*{!isDialog && renderForm()}*/}
            <LrDialog
                title={'Taxes and Fees Missing'}
                isOpen={confirmOpen}
                onSubmit={confirmSubmit}
                onCancel={() => setConfirmOpen(false)}
                submitButtonText={'OK'}
            >
                <Typography>Taxes and Fees are both empty, continue anyway?</Typography>
            </LrDialog>
        </>
    );
};

export default TaxesDialog;
